import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactFlow, {
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Controls,
  Background,
  MiniMap,
  ConnectionMode,
} from 'reactflow';
import { useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';

import './graph-editor.css';

import QuestionNode from './nodes/QuestionNode';
import PackageNode from './nodes/PackageNode';
import StartNode from './nodes/StartNode';
import EndNode from './nodes/EndNode';
import DecisionNode from './nodes/DecisionNode';
import MediaNode from './nodes/MediaNode';
import PleasantNode from './nodes/PleasantNode';
import ThickPathEdge from './ThickPathEdge';
import { renameNodeIds } from '../../utils/graphMutations';
import { registerCanvasCenterProvider } from '../../utils/canvasViewport';
import { variableNameFromId } from '../../utils/variableNames';
import { ptToPx } from '../../constants/canvasSettings';
// Removed well-formed validator usage
import { useNotifications } from '../Notifications/NotificationContext';


// DEBUG helper
const dbg = (tag, payload) => console.log('[GraphEditor]', tag, payload);

const nodeTypes = {
  question: QuestionNode,
  package: PackageNode,
  start: StartNode,
  end: EndNode,
  decision: DecisionNode,
  media: MediaNode,
  pleasant: PleasantNode,
};

const edgeTypes = {
  default: ThickPathEdge,
  smoothstep: ThickPathEdge,
  step: ThickPathEdge,
  straight: ThickPathEdge,
};

const GraphEditor = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onSelectionChange,
  errorNodeIds = new Set(),
  warningNodeIds = new Set(),
  issueByNode = new Map(),
  variableMap = new Map(),
  canvasSettings = { background: '#FFFFFF', shadeByType: false, defaultEdgeType: 'smoothstep' },
}) => {
  // Remember last selection to prevent log spam
  const lastSigRef = useRef(null);
  const [menu, setMenu] = useState({ show: false, x: 0, y: 0, kind: null, targetId: null });
  const lastClickTime = useRef(0);
  const lastClickPos = useRef({ x: 0, y: 0 });

  // ---------------- callbacks ----------------

  // React Flow instance helpers must be initialized before any callbacks that capture them
  const { project, addNodes, setCenter, getZoom, fitView } = useReactFlow();

  // Publish "where is the middle of the visible canvas" so the Toolbar (which
  // sits outside the ReactFlowProvider) can drop new nodes there.
  useEffect(() => {
    registerCanvasCenterProvider(() => {
      const pane = document.querySelector('.react-flow');
      if (!pane) return null;
      const rect = pane.getBoundingClientRect();
      return project({ x: rect.width / 2, y: rect.height / 2 });
    });
    return () => registerCanvasCenterProvider(null);
  }, [project]);

  // Refit the viewport after a layout cleanup (App recomputes positions,
  // then asks us to fit once the new nodes have rendered).
  useEffect(() => {
    function onFitView() {
      try { setTimeout(() => fitView({ duration: 400, padding: 0.15 }), 120); } catch (_) { }
    }
    window.addEventListener('graph-fit-view', onFitView);
    return () => window.removeEventListener('graph-fit-view', onFitView);
  }, [fitView]);

  const handleNodesChange = useCallback(
    (changes) => {
      let effectiveChanges = changes || [];

      // Variable reference guard: if a removed node defines a variable still
      // referenced by surviving nodes, ask before destroying the reference.
      try {
        const removes = effectiveChanges.filter(c => c && c.type === 'remove');
        if (removes.length && variableMap && variableMap.size) {
          const blocked = new Set();
          const nodeById = new Map(nodes.map(n => [n.id, n]));
          for (const change of removes) {
            const node = nodeById.get(change.id);
            if (!node) continue;
            const nodeLabel = node.data?.id || node.id;
            const removedIdsAll = new Set(removes.map(c => c.id));
            const conflicts = [];
            for (const rec of variableMap.values()) {
              if (!rec.definedAt.includes(nodeLabel)) continue;
              const survivingUses = rec.usedAt.filter(usedLabel => {
                // Resolve label back to a node id to check survival
                const usedNode = nodes.find(n => (n.data?.id || n.id) === usedLabel);
                if (!usedNode) return false;
                return !removedIdsAll.has(usedNode.id);
              });
              if (survivingUses.length > 0) {
                conflicts.push({ name: rec.name, usedAt: survivingUses });
              }
            }
            if (conflicts.length) {
              const summary = conflicts.map(c => `  • ${c.name} (used by: ${c.usedAt.join(', ')})`).join('\n');
              const proceed = window.confirm(
                `Node ${nodeLabel} defines variables still referenced elsewhere:\n${summary}\n\nDelete anyway?`
              );
              if (!proceed) blocked.add(change.id);
            }
          }
          if (blocked.size) {
            effectiveChanges = effectiveChanges.filter(c => !(c.type === 'remove' && blocked.has(c.id)));
          }
        }
      } catch (_) { /* silent */ }

      const updated = applyNodeChanges(effectiveChanges, nodes);
      onNodesChange(updated);
      // Auto-remove incident edges for nodes removed via canvas actions
      try {
        const removedIds = effectiveChanges.filter(c => c && c.type === 'remove').map(c => c.id);
        if (removedIds.length) {
          const filtered = (edges || []).filter(e => !removedIds.includes(e.source) && !removedIds.includes(e.target));
          if (filtered.length !== (edges || []).length) onEdgesChange(filtered);
        }
      } catch (_) { /* silent */ }
    },
    [nodes, edges, onNodesChange, onEdgesChange, variableMap]
  );

  const { showError, showSuccess, showWarning } = useNotifications();

  // Helper to commit a node id rename, invoked by parent/editor after user confirmation
  // Usage: window.dispatchEvent(new CustomEvent('graph-rename-node-id', { detail: { oldId, newId } }))
  useEffect(() => {
    function onRenameId(e) {
      try {
        const { oldId, newId } = e?.detail || {};
        if (!oldId || !newId) return;
        const { nodes: n2, edges: e2 } = renameNodeIds({ nodes, edges }, { [String(oldId)]: String(newId) });
        onNodesChange(n2);
        onEdgesChange(e2);
        // Post-rename notification only (validator removed)
        showSuccess(`Renamed '${oldId}'→'${newId}'.`);
      } catch (err) {
        // Surface minimal error – production UI can show a toast
        console.error('ID rename failed:', err.message);
        showError(`ID rename failed: ${err.message}`);
      }
    }
    window.addEventListener('graph-rename-node-id', onRenameId);
    return () => window.removeEventListener('graph-rename-node-id', onRenameId);
  }, [nodes, edges, onNodesChange, onEdgesChange, showError, showSuccess, showWarning]);

  const handleEdgesChange = useCallback(
    (changes) => {
      const updated = applyEdgeChanges(changes, edges);
      onEdgesChange(updated);
    },
    [edges, onEdgesChange]
  );

  const onConnect = useCallback(
    (params) => {
      const styleId = canvasSettings.defaultEdgeType || 'smoothstep';
      const newEdge = {
        ...params,
        id: `edge-${Date.now()}`,
        type: styleId,
        data: { condition: '', label: '', edgeType: styleId },
      };
      onEdgesChange(addEdge(newEdge, edges));
    },
    [edges, onEdgesChange, canvasSettings.defaultEdgeType]
  );

  // Listen for requests to create a paired media hide node and connect it
  useEffect(() => {
    function onCreatePairedHide(e) {
      try {
        const detail = e?.detail || {};
        const showNodeId = detail.showNodeId;
        const stimulusId = String(detail.stimulus_id || '').trim();
        if (!showNodeId || !stimulusId) return;
        const showNode = nodes.find(n => n.id === showNodeId);
        if (!showNode) return;
        const now = Date.now();
        const newNodeId = `media_hide_${now}`;
        const newNode = {
          id: newNodeId,
          type: 'media',
          position: { x: (showNode.position?.x || 100) + 60, y: (showNode.position?.y || 100) + 160 },
          data: {
            id: `H_${stimulusId}`,
            text: `Hide ${stimulusId}`,
            type: 'media',
            show: false,
            stimulus_id: stimulusId,
            media: { src: '' },
          },
          selected: true,
        };
        const styleId = canvasSettings.defaultEdgeType || 'smoothstep';
        const newEdge = {
          id: `edge-${now}`,
          source: showNodeId,
          target: newNodeId,
          type: styleId,
          data: { condition: '', label: '', edgeType: styleId },
        };
        onNodesChange([...nodes, newNode]);
        onEdgesChange([...edges, newEdge]);
        onSelectionChange({ selectedNode: newNode, selectedEdge: null });
      } catch (_) { }
    }
    window.addEventListener('graph-create-media-hide', onCreatePairedHide);
    return () => window.removeEventListener('graph-create-media-hide', onCreatePairedHide);
  }, [nodes, edges, onNodesChange, onEdgesChange, onSelectionChange, canvasSettings.defaultEdgeType]);

  // Forward selection ONLY when it actually changes (dedup)
  const handleSelectionChange = useCallback(
    ({ nodes: selNodes, edges: selEdges }) => {
      const sig = JSON.stringify([
        selNodes.map((n) => n.id),
        selEdges.map((e) => e.id),
      ]);
      if (sig === lastSigRef.current) {
        return;
      }
      lastSigRef.current = sig;
      dbg('selectionChange', { selNodes, selEdges });

      // Forward the whole selection. The singular fields are a convenience for
      // the editor panels and are only set when the selection is unambiguous.
      onSelectionChange({
        selectedNodes: selNodes,
        selectedEdges: selEdges,
        selectedNode: selNodes.length === 1 ? selNodes[0] : null,
        selectedEdge: selNodes.length === 0 && selEdges.length === 1 ? selEdges[0] : null,
      });
    },
    [onSelectionChange]
  );

  // Center/zoom to target node when requested by Variables panel, Validation panel, etc.
  useEffect(() => {
    function onCenterNode(e) {
      try {
        const nodeId = e?.detail?.nodeId;
        if (!nodeId) return;
        const targetNode = nodes.find(n => n.id === nodeId || n.data?.id === nodeId);
        if (!targetNode) return;
        // Use ReactFlow's setCenter to properly pan the viewport
        const x = (targetNode.position?.x || 0) + (targetNode.width || 200) / 2;
        const y = (targetNode.position?.y || 0) + (targetNode.height || 80) / 2;
        const currentZoom = getZoom();
        // Clamp zoom: at least 0.8 so the user can actually see the node
        const zoom = Math.max(currentZoom, 0.8);
        setCenter(x, y, { zoom, duration: 400 });

        // Brief highlight so the user spots it
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-id="${targetNode.id}"]`);
          if (el) {
            el.style.outline = '3px solid #667eea';
            el.style.boxShadow = '0 0 14px rgba(102,126,234,0.7)';
            setTimeout(() => { el.style.outline = ''; el.style.boxShadow = ''; }, 1200);
          }
        });
      } catch (_) { }
    }
    window.addEventListener('graph-center-node', onCenterNode);
    return () => window.removeEventListener('graph-center-node', onCenterNode);
  }, [nodes, setCenter, getZoom]);

  // Temporary highlight for target node and dim others
  useEffect(() => {
    function onWfHighlight(e) {
      try {
        const nodeId = e?.detail?.nodeId;
        if (!nodeId) return;
        const dimColor = 'rgba(0,0,0,0.15)';
        // apply style dimming via data attribute
        const canvas = document.querySelector('.react-flow');
        if (!canvas) return;
        const allNodes = canvas.querySelectorAll('.react-flow__node');
        allNodes.forEach(el => {
          if (el.getAttribute('data-id') === nodeId) {
            el.style.boxShadow = '0 0 12px rgba(220, 53, 69, 0.8)';
            el.style.outline = '3px solid #dc3545';
          } else {
            el.style.opacity = '0.35';
          }
        });
        setTimeout(() => {
          allNodes.forEach(el => { el.style.opacity = ''; el.style.outline = ''; el.style.boxShadow = ''; });
        }, 1200);
      } catch (_) { }
    }
    window.addEventListener('wf-highlight', onWfHighlight);
    return () => window.removeEventListener('wf-highlight', onWfHighlight);
  }, []);

  // Hover spotlight from WellFormedPanel
  useEffect(() => {
    function onWfHover(e) {
      try {
        const nodeId = e?.detail?.nodeId || null;
        const canvas = document.querySelector('.react-flow');
        if (!canvas) return;
        const allNodes = canvas.querySelectorAll('.react-flow__node');
        if (!nodeId) {
          allNodes.forEach(el => { el.style.opacity = ''; });
          return;
        }
        allNodes.forEach(el => {
          el.style.opacity = (el.getAttribute('data-id') === nodeId) ? '' : '0.35';
        });
      } catch (_) { }
    }
    window.addEventListener('wf-hover', onWfHover);
    return () => window.removeEventListener('wf-hover', onWfHover);
  }, []);

  // Deselect everything when clicking on empty canvas
  const onPaneClick = useCallback((evt) => {
    if (evt && evt.target && evt.target.classList && evt.target.classList.contains('react-flow__pane')) {
      try { console.log('[GraphEditor] paneClick'); } catch (_) { }

      const now = Date.now();
      const timeSinceLastClick = now - (lastClickTime.current || 0);
      const dx = (evt.clientX || 0) - (lastClickPos.current?.x || 0);
      const dy = (evt.clientY || 0) - (lastClickPos.current?.y || 0);
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (timeSinceLastClick < 400 && distance < 10) {
        // Double-click detected → create a node
        try { console.log('[GraphEditor] DOUBLE-CLICK detected, creating node'); } catch (_) { }
        try {
          const paneBounds = (evt.currentTarget && evt.currentTarget.getBoundingClientRect)
            ? evt.currentTarget.getBoundingClientRect()
            : (document.querySelector('.react-flow')?.getBoundingClientRect() || { left: 0, top: 0 });
          const position = project({ x: evt.clientX - paneBounds.left, y: evt.clientY - paneBounds.top });
          const nodeId = `Q${Date.now()}`;
          const newNode = {
            id: nodeId,
            type: 'question',
            position,
            // A question created here collects input, so it needs a variable
            // name from the start — without one the kernel refuses to load the
            // survey (VARIABLE_REQUIRED).
            data: {
              id: nodeId,
              text: '',
              type: 'text',
              required: false,
              variable: variableNameFromId(nodeId),
            },
            selected: true,
          };
          if (typeof addNodes === 'function') {
            addNodes([newNode]);
          }
          onSelectionChange({ selectedNode: newNode, selectedEdge: null });
        } catch (err) {
          try { console.error('[GraphEditor] double-click node creation failed', err); } catch (_) { }
        }
        // Reset to prevent triple-click from triggering another node
        lastClickTime.current = 0;
      } else {
        // Single-click: clear selection and track click for dbl-click detection
        onSelectionChange({ selectedNode: null, selectedEdge: null });
        setMenu(m => ({ ...m, show: false }));
        lastClickTime.current = now;
        lastClickPos.current = { x: evt.clientX || 0, y: evt.clientY || 0 };
      }
    }
  }, [onSelectionChange, project, addNodes]);

  // OBSOLETE: React Flow's onPaneDoubleClick may not fire reliably; using onPaneClick timing detection instead
  // (intentionally no onPaneDoubleClick handler)


  return (
    <div
      className={`graph-editor${canvasSettings.shadeByType ? ' shade-by-type' : ''}`}
      style={{
        background: canvasSettings.background || '#FFFFFF',
        // Canvas font sizes (user preference, set in Canvas appearance dialog)
        '--node-title-font-size': `${ptToPx(canvasSettings.nodeTitlePt || 9)}px`,
        '--node-text-font-size': `${ptToPx(canvasSettings.nodeTextPt || 10)}px`,
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onPaneClick={onPaneClick}
        onSelectionChange={handleSelectionChange}
        multiSelectionKeyCode="Shift"
        selectionOnDrag={false}
        zoomOnDoubleClick={false}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        minZoom={0.1}
        maxZoom={8}
        fitView
        attributionPosition="bottom-left"
        onContextMenu={(e) => {
          try {
            e.preventDefault();
            const el = e.target.closest('.react-flow__node, .react-flow__edge');
            if (!el) { setMenu({ show: true, x: e.clientX, y: e.clientY, kind: 'canvas', targetId: null }); return; }
            const id = el.getAttribute('data-id');
            if (el.classList.contains('react-flow__node')) {
              setMenu({ show: true, x: e.clientX, y: e.clientY, kind: 'node', targetId: id });
            } else {
              setMenu({ show: true, x: e.clientX, y: e.clientY, kind: 'edge', targetId: id });
            }
          } catch (_) { }
        }}
      >
        <Background variant="dots" gap={20} size={1} />
        <Controls showInteractive={true} position="bottom-left" />
        <MiniMap
          nodeStrokeColor={(n) => errorNodeIds.has(n.id) ? '#dc3545' : warningNodeIds.has(n.id) ? '#ffc107' : '#333'}
          nodeColor={(n) => errorNodeIds.has(n.id) ? '#f8d7da' : warningNodeIds.has(n.id) ? '#fff3cd' : '#fff'}
          nodeBorderRadius={8}
          maskColor="rgba(0, 0, 0, 0.1)"
          position="bottom-right"
        />
        {/* Simple node badges (absolute positioned divs) */}
        {nodes.map(n => {
          const rec = issueByNode.get(n.id);
          if (!rec) return null;
          const color = rec.level === 'error' ? '#dc3545' : '#ffc107';
          const bg = rec.level === 'error' ? '#f8d7da' : '#fff3cd';
          return (
            <div key={`badge-${n.id}`} style={{ position: 'absolute', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }} data-id={`badge-${n.id}`}>
              <div style={{
                display: 'inline-block', padding: '2px 6px', borderRadius: 12, border: `1px solid ${color}`, background: bg, color: '#333', fontSize: 11
              }} title={rec.codes.join(', ')}>
                {rec.codes.slice(0, 2).join(', ')}{rec.codes.length > 2 ? '…' : ''}
                {rec.manual && (
                  <span style={{ marginLeft: 6, color: '#666', fontStyle: 'italic' }}>(manual)</span>
                )}
              </div>
            </div>
          );
        })}
        {menu.show && (
          <div style={{ position: 'fixed', top: menu.y, left: menu.x, background: '#fff', border: '1px solid #ccc', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1000 }} onMouseLeave={() => setMenu(m => ({ ...m, show: false }))}>
            {menu.kind === 'node' && (
              <>
                <button className="btn-small" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }} title="Pan and zoom to this node" onClick={() => { try { window.dispatchEvent(new CustomEvent('graph-center-node', { detail: { nodeId: menu.targetId } })); } catch (_) { }; setMenu(m => ({ ...m, show: false })); }}>Jump here</button>
                <button className="btn-small" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }} title="Attempt to auto-fix validation issues" onClick={() => { try { window.dispatchEvent(new CustomEvent('wf-autofix-request')); } catch (_) { }; setMenu(m => ({ ...m, show: false })); }}>Auto-fix…</button>
              </>
            )}
            {menu.kind === 'edge' && (
              <>
                {(() => {
                  const edge = edges.find(ed => ed.id === menu.targetId);
                  const from = edge?.source; const to = edge?.target;
                  return (
                    <>
                      <button className="btn-small" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }} title="Open edge condition editor" onClick={() => { try { window.dispatchEvent(new CustomEvent('graph-select-edge', { detail: { from, to } })); } catch (_) { }; setMenu(m => ({ ...m, show: false })); }}>Open conditions</button>
                      <button className="btn-small" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }} title="Pan and zoom to the target node" onClick={() => { try { window.dispatchEvent(new CustomEvent('graph-center-node', { detail: { nodeId: to } })); } catch (_) { }; setMenu(m => ({ ...m, show: false })); }}>Jump to target</button>
                    </>
                  );
                })()}
              </>
            )}
            {menu.kind === 'canvas' && (
              <>
                <button className="btn-small" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }} title="Arrange nodes so the flow reads top to bottom from Start" onClick={() => { try { window.dispatchEvent(new CustomEvent('graph-cleanup-layout')); } catch (_) { }; setMenu(m => ({ ...m, show: false })); }}>Clean up layout</button>
                <button className="btn-small" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }} title="Connector style, background color, and node shading" onClick={() => { try { window.dispatchEvent(new CustomEvent('graph-open-appearance')); } catch (_) { }; setMenu(m => ({ ...m, show: false })); }}>Canvas appearance…</button>
                <button className="btn-small" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }} title="Review and apply fixes for validation issues" onClick={() => { try { window.dispatchEvent(new CustomEvent('wf-autofix-request')); } catch (_) { }; setMenu(m => ({ ...m, show: false })); }}>Auto-fix…</button>
              </>
            )}
          </div>
        )}
      </ReactFlow>
    </div>
  );
};

export default GraphEditor;
